import React, { useState } from 'react';

import OrderDisabled from '@blocks/_common/order-disabled';
import useAuthModal from '@blocks/hooks/useAuthModal';
import { createLogout, useSessionStore } from '@blocks/stores/session';
import withErrorBoundary from '@blocks/utils/withErrorBoundary';

import {
  CLIENT_TYPE_BROWSER,
  DOMAIN_DEFAULT,
  INITIAL_PAPER_TYPE,
  PAGES_MIN,
  WORDS_MIN,
} from '../constants';
import gaConfig from '../constants/ga-config';
import { useOnSubmit } from '../hooks';
import useMediaQuery from '../hooks/useMediaQuery';
import { getDefaultDate, isBrowser, toBoolean } from '../utils';
import {
  handleGaLogout,
  handleGaNewOrder,
  handleGaSignIn,
  handleGaSignUp,
} from '../utils/gtm/gtm-events';

import { setOrder } from './actions';
import { DISABLED_ROLES, subscription } from './constants';
import { createGDPRShowByDefaultDecorator } from './decorators';
import FormWrapper from './form-base';
import OrderForm from './order-form';
import onSubmit from './submit';
import { OrderProps, orderPropTypes } from './types';

import '../styles/global.css';

export const initialValues = {
  login: '',
  paper_type: INITIAL_PAPER_TYPE,
  pages_qty: PAGES_MIN,
  words_qty: WORDS_MIN,
  'paper-size-type': 'pages',
  deadline_datetime: getDefaultDate(),
  stay_signed_in: true,
  writer_req_slug: '',
  policy_agree: false,
  marketing_agree: false,
  title: '',
  client_type: CLIENT_TYPE_BROWSER,
  captcha_token: '',
  login_ability: false,
  showWriter: false,
  showGdpr: false,
  showTitle: false,
};

const Order: React.FC<OrderProps> = ({
  domain = DOMAIN_DEFAULT,
  name = 'order',
  showGdprByDefault = true,
  submitText = 'Place your order',
  requestText = 'Request writer',
  loginText = 'Log into your account',
  modalTitle = 'Log into your account',
  forgotPasswordLink = '/auth_password_reset.html',
  showRecaptchaHint = true,
  mobileFlowBreakpoint = 0,
  debugOAuth = false,
  debugOAuthProviderUsed = '',
  shortMode = false,
  enableOAuthInitializationPreview = false,
  ...rest
}) => {
  const [loading, setLoading] = useState(false);
  const { loginModal, openAuthModal } = useAuthModal(domain);

  const applyMobileFlow = useMediaQuery(`(max-width: ${mobileFlowBreakpoint}px)`);
  const {
    session: { user_role },
  } = useSessionStore(domain);
  const logoutHandler = createLogout({
    domain,
    onLogout: handleGaLogout(gaConfig),
  });

  const submitHandler = useOnSubmit({
    domain,
    onSubmit,
    onSignIn: handleGaSignIn(gaConfig, { method: 'default' }),
    onSignUp: handleGaSignUp(gaConfig, { method: 'default' }),
    onNewOrder: handleGaNewOrder(gaConfig),
    openAuthModal,
    setLoading,
  });

  return (
    <>
      {user_role && DISABLED_ROLES.includes(user_role) ? (
        <OrderDisabled
          user_role={user_role}
          logoutHandler={logoutHandler}
        />
      ) : (
        <FormWrapper
          initialValues={{
            ...initialValues,
            domain,
            name,
            showGdpr: toBoolean(showGdprByDefault),
            showGdprByDefault: toBoolean(showGdprByDefault),
          }}
          decorators={toBoolean(showGdprByDefault) ? [] : [createGDPRShowByDefaultDecorator]}
          onSubmit={submitHandler}
          subscription={subscription}
          render={({ handleSubmit, submitting }) => (
            <OrderForm
              domain={domain}
              name={name}
              loading={loading}
              setLoading={setLoading}
              submitting={submitting}
              handleSubmit={handleSubmit}
              loginModal={loginModal}
              debugOAuth={debugOAuth}
              logoutHandler={logoutHandler}
              debugOAuthProviderUsed={debugOAuthProviderUsed}
              shortMode={shortMode}
              submitText={submitText}
              requestText={requestText}
              loginText={loginText}
              modalTitle={modalTitle}
              forgotPasswordLink={forgotPasswordLink}
              showRecaptchaHint={showRecaptchaHint}
              enableOAuthInitializationPreview={enableOAuthInitializationPreview}
              applyMobileFlow={applyMobileFlow}
              {...rest}
            />
          )}
        />
      )}
    </>
  );
};

Order.propTypes = orderPropTypes;

if (isBrowser()) {
  // @ts-ignore
  window.bbSetOrder = setOrder;
}

export default withErrorBoundary(Order);
